import { IChartApi, SeriesMarker, Time } from 'lightweight-charts';
import { useReducer, useCallback, MutableRefObject, useRef, useEffect } from 'react';
import { RangeEnum } from '../../../model/range.enum';
import { SeriesItem, seriesReducer, SeriesState } from '../series-reducer';
import { lightBlue, primary } from '../../../theme/colors';
import { useCreateSeries } from '../../../widgets/immersive/chart/hooks/use-create-series';
import { ChartTypeEnum } from '../../../model/chart_type.enum';
import { PERPETUAL_ID } from '../market-data.constants';
import { useChartSetup } from '../../../widgets/immersive/chart/hooks/use-chart-setup';
import { normalizeData } from '../market-data.helpers';
import Utils from '../../../utils/immersive_utils';

type ISeriesManager = {
  chart: MutableRefObject<IChartApi | undefined>;
  chartExtra: MutableRefObject<IChartApi | undefined>;
  chartType: ChartTypeEnum;
  chartColorsMap: {
    colors: MutableRefObject<string[]>;
    volSeriesColorMap: MutableRefObject<Map<string, string>>;
    flySeriesColorMap: MutableRefObject<Map<string, string>>;
    futureSeriesColorMap: MutableRefObject<Map<string, string>>;
    resetColors: () => void;
  };
  range: RangeEnum;
  seriesRef: MutableRefObject<SeriesState>;
  chartExtraContainerRef: MutableRefObject<HTMLDivElement | null>;
  setHasFlyChart: (value: boolean) => void;
};

export type IAddSeries = {
  rawId: string;
  type: string;
  label: string;
  perpetualCallback?: () => Promise<void> | undefined;
  seriesVolsCallback?: (queries: { label: string; tenor: string }[]) => void;
  force?: boolean;
  maintainedColor?: string;
};

export const useSeriesManager = ({
  chart,
  chartExtra,
  chartType = ChartTypeEnum.Area,
  chartColorsMap,
  range,
  seriesRef,
  chartExtraContainerRef,
  setHasFlyChart,
}: ISeriesManager) => {
  const [series, seriesDispatch] = useReducer(seriesReducer, {});
  const hasZeroLineOnExtraChart = useRef(false);
  const createSeries = useCreateSeries(chart);
  const { colors, volSeriesColorMap, flySeriesColorMap, futureSeriesColorMap, resetColors } =
    chartColorsMap;
  const { initFlyChart } = useChartSetup();

  const getZeroLine: any = () => {
    const zeroLine = {
      price: 0,
      color: primary[6], //cyan06
      lineWidth: 2,
      lineStyle: 1,
      axisLabelVisible: true,
      title: '',
    };

    return zeroLine;
  };

  const pickColorFor = (id: string, type: string): string => {
    if (!chart.current) return '';

    const map =
      type === 'vol'
        ? volSeriesColorMap
        : type === 'fly' && chartExtra.current
          ? flySeriesColorMap
          : futureSeriesColorMap;

    if (!map) return '';

    if (map.current.has(id)) {
      return map.current.get(id) as string;
    }
    const usedColors = new Set<string>([
      ...Array.from(volSeriesColorMap.current.values()),
      ...Array.from(flySeriesColorMap.current.values()),
      ...Array.from(futureSeriesColorMap.current.values()),
    ]);

    const palette = colors.current;
    let picked: string | undefined;
    if (palette && palette.length) {
      const idx = palette.findIndex((c) => !usedColors.has(c));
      if (idx >= 0) {
        picked = palette.splice(idx, 1)[0];
      } else {
        picked = palette.shift();
      }
    }

    const color = picked || primary[6];
    if (color) {
      map.current.set(id, color);
    }
    return color;
  };

  useEffect(() => {
    if (chartExtra.current) return;
    const flyCount = Object.values(series).filter((s) => s.type === 'fly').length;

    if (!chartExtra.current && chartExtraContainerRef.current && flyCount === 0) {
      initFlyChart({
        containerRef: chartExtraContainerRef,
        chartRef: chartExtra,
        actions: {},
        options: {
          autoSize: false,
        },
      });
    }
  }, [series]);

  const makeQuerie = (id: string) => {
    const parts = id.split('_');
    if (parts.length >= 3) {
      return { label: parts[1], tenor: parts[2] };
    }
    return null;
  };

  const addSeries = useCallback(
    async ({
      rawId,
      type,
      label,
      perpetualCallback,
      seriesVolsCallback,
      force = false,
      maintainedColor,
    }: IAddSeries): Promise<void> => {
      const id = rawId;
      if (seriesRef.current[id]) {
        if (!force) return;
        const existing = seriesRef.current[id];
        try {
          const targetChart = existing.type === 'fly' ? chartExtra.current : chart.current;
          targetChart?.removeSeries(existing.seriesApi);
        } catch {
          // noop
        }
        seriesDispatch({ type: 'REMOVE', payload: { id } });
      }
      const isFly = type === 'fly';
      const isFuture = id.includes('_FUTURE');
      const color = maintainedColor || pickColorFor(id, type);
      if (maintainedColor) {
        const targetMap =
          type === 'vol'
            ? volSeriesColorMap
            : type === 'fly' && chartExtra.current
              ? flySeriesColorMap
              : futureSeriesColorMap;
        if (targetMap) {
          targetMap.current.set(id, maintainedColor);
        }
        const palette = colors.current;
        const idx = palette.indexOf(maintainedColor);
        if (idx !== -1) palette.splice(idx, 1);
      }
      const isPerp = type === 'perp';
      const targetChart = type === 'fly' ? chartExtra.current! : chart.current!;
      if (!chart.current) return;

      if (isPerp && perpetualCallback) {
        const perpSeriesApi = createSeries(chartType);

        perpSeriesApi.applyOptions({
          priceFormat: { type: 'custom', formatter: (p: number) => Utils.formatNumber(p, 2) },
        });

        seriesDispatch({
          type: 'ADD',
          payload: {
            id,
            type,
            chartType,
            withMarkers: chartType === ChartTypeEnum.LineWMarkers,
            seriesApi: perpSeriesApi,
            color: '',
            label,
            data: [],
          },
        });

        await perpetualCallback();
        return;
      }

      const options: any = {
        color,
        priceLineVisible: false,
        priceScaleId: isFly || isFuture ? 'right' : 'left',
        lineType: 2,
        lineWidth: 1,
        priceFormat: isFuture
          ? { type: 'custom', formatter: (p: number) => Utils.formatNumber(p, 2) }
          : { type: 'custom', formatter: (p: number) => `${p.toFixed(2)}%` },
      };

      const seriesApi = targetChart.addLineSeries(options);

      if (isFly) setHasFlyChart(true);
      if (isFly && !hasZeroLineOnExtraChart.current) {
        seriesApi.createPriceLine(getZeroLine());
        hasZeroLineOnExtraChart.current = true;
      }

      const payload = { id, type, seriesApi, color, label, data: [] };
      seriesDispatch({ type: 'ADD', payload });

      if (seriesVolsCallback) {
        const queries: any = makeQuerie(id);
        seriesVolsCallback([queries]);
      }
    },
    [chart, chartExtra, chartType, range, pickColorFor, setHasFlyChart],
  );

  const removePerpetual = useCallback(() => {
    const entry = Object.entries(seriesRef.current).find(([, item]) => item.type === 'perp');
    if (!entry) return;
    const [perpId, perpItem] = entry;

    chart.current?.removeSeries(perpItem.seriesApi);
    seriesDispatch({ type: 'REMOVE', payload: { id: perpId } });
  }, []);

  const removeSeries = useCallback(
    (id: string) => {
      const current = seriesRef.current;
      const item = current[id];
      if (!item) {
        return;
      }

      const chartApi = item.type === 'fly' ? chartExtra.current : chart.current;
      chartApi?.removeSeries(item.seriesApi);

      const colorMap =
        item.type === 'vol'
          ? chartColorsMap.volSeriesColorMap
          : item.type === 'fly'
            ? chartColorsMap.flySeriesColorMap
            : chartColorsMap.futureSeriesColorMap;
      colorMap.current.delete(id);

      const newSeries = { ...current };
      delete newSeries[id];

      const hasPlainVol = Object.values(newSeries).some(
        (s) => s.type === 'vol' && !s.id.includes('_FUTURE'),
      );
      chart.current?.applyOptions({
        leftPriceScale: { visible: hasPlainVol },
        rightPriceScale: { visible: true },
      });

      if (chartExtra.current) {
        chartExtra.current.applyOptions({
          leftPriceScale: { visible: hasPlainVol },
        });
      }

      const hasFly = Object.values(newSeries).some((s) => s.type === 'fly');
      if (!hasFly) {
        setHasFlyChart(false);
        hasZeroLineOnExtraChart.current = false;
      }

      const nonPerpCount = Object.keys(newSeries).filter((k) => k !== PERPETUAL_ID).length;
      if (nonPerpCount === 0) {
        chartColorsMap.resetColors();
      }

      seriesDispatch({ type: 'REMOVE', payload: { id } });
    },
    [series, seriesDispatch, chartColorsMap, setHasFlyChart],
  );

  const removeAllCmvolSeries = useCallback(() => {
    Object.entries(series).forEach(([id, item]) => {
      if (id === PERPETUAL_ID) {
        return;
      }

      const targetChart = item.type === 'fly' ? chartExtra.current : chart.current;
      if (targetChart && item.seriesApi) {
        targetChart.removeSeries(item.seriesApi);
      }
      seriesDispatch({ type: 'REMOVE', payload: { id } });
    });

    const hasFly = Object.values(series).some((s) => s.type === 'fly');
    if (hasFly) {
      setHasFlyChart(false);
      hasZeroLineOnExtraChart.current = false;
    }

    chartColorsMap.volSeriesColorMap.current.clear();
    chartColorsMap.flySeriesColorMap.current.clear();
    chartColorsMap.futureSeriesColorMap.current.clear();

    chart.current?.applyOptions({
      leftPriceScale: { visible: false },
      rightPriceScale: { visible: true },
    });
  }, [series, seriesDispatch, setHasFlyChart]);

  const updateSeries = useCallback(
    (
      id: string,
      data: SeriesItem['data'],
      options: { isBackward?: boolean; isRefresh?: boolean } = {},
    ) => {
      const currentSeries = seriesRef.current;
      if (!chart.current || !currentSeries[id]) return;
      try {
        const item = currentSeries[id]!;
        const { seriesApi, type, withMarkers } = item;
        const isFuture = id.includes('_FUTURE');
        const isPerp = type === 'perp';
        const isVol = type === 'vol';
        const isFly = type === 'fly';

        if (!isPerp) {
          const mainOpts: any = {};
          const extraOpts: any = {};

          if (isVol) mainOpts.leftPriceScale = { visible: true };
          if (isFuture) mainOpts.rightPriceScale = { visible: true };
          if (isVol) extraOpts.leftPriceScale = { visible: true };
          if (isFuture) extraOpts.leftPriceScale = { visible: true };
          if (isFly) extraOpts.rightPriceScale = { visible: true };

          if (Object.keys(mainOpts).length) chart.current.applyOptions(mainOpts);
          if (Object.keys(extraOpts).length) chartExtra.current?.applyOptions(extraOpts);
        }

        const existing = options.isBackward ? (seriesRef.current[id]?.data ?? []) : [];
        const normalized = normalizeData(options.isBackward ? [...data, ...existing] : data);

        seriesApi.setData(normalized);
        seriesDispatch({ type: 'UPDATE', payload: { id, data: normalized } });

        if (withMarkers) {
          const markers: SeriesMarker<Time>[] = normalized.map((pt) => ({
            time: pt.time as Time,
            position: 'inBar',
            color: lightBlue,
            shape: 'circle',
            size: 0.2,
          }));
          seriesApi.setMarkers(markers);
        }

        if (isPerp && !options.isBackward) {
          chart.current?.timeScale().fitContent();
          const logical = chart.current?.timeScale().getVisibleLogicalRange?.();
          if (logical) {
            chartExtra.current?.timeScale().setVisibleLogicalRange(logical);
          }
        }
      } catch (error) {
        console.error('Error updating series:', error);
      }
    },
    [chartType],
  );

  const appendSeriesTick = useCallback((id: string, tick: any) => {
    const item = seriesRef.current[id];
    if (!item) return;

    const last = item.data[item.data.length - 1]?.time;
    if (tick.time <= last) return;

    if (item.type === 'perp') {
      (item.seriesApi as any).update(tick);
    } else {
      item.seriesApi.update({ time: tick.time, value: tick.value });
    }

    seriesDispatch({ type: 'APPEND_TICK', payload: { id, tick } });
  }, []);

  const prependSeriesData = useCallback((id: string, newData: any[]) => {
    const item = seriesRef.current[id];
    if (!item) return;
    const merged = [...newData, ...item.data]
      .reduce<any[]>((acc, pt) => {
        if (!acc.length || acc[acc.length - 1].time !== pt.time) acc.push(pt);
        return acc;
      }, [])
      .sort((a, b) => a.time - b.time);
    item.seriesApi.setData(merged);
    seriesDispatch({ type: 'UPDATE', payload: { id, data: merged } });
  }, []);

  const removeSeriesApi = useCallback(
    (item: SeriesItem) => {
      const target = item.type === 'fly' ? chartExtra.current : chart.current;
      target?.removeSeries(item.seriesApi);
    },
    [chart, chartExtra],
  );

  const resetAll = useCallback(() => {
    Object.values(series).forEach(removeSeriesApi);
    seriesDispatch({ type: 'RESET' });
    resetColors();
  }, [series, removeSeriesApi, resetColors]);

  return {
    series,
    seriesDispatch,
    addSeries,
    removePerpetual,
    removeSeries,
    removeAllCmvolSeries,
    updateSeries,
    appendSeriesTick,
    prependSeriesData,
    resetAll,
  };
};
